import {
  TINT_DEFAULTS,
  TINTED_TEXTURES,
  blockTextureSrc,
  drawOver,
  fetchRaster,
  firstFrame,
  multiplyTint,
  shrinkBy,
  type Raster,
} from './icons';
import type { Rgb } from './types';

export type FaceName = 'up' | 'down' | 'north' | 'south' | 'west' | 'east';

/** Same order as the preview shader's face numbers, so a face's layer is `material * 6 + face`. */
export const FACE_ORDER: FaceName[] = ['up', 'down', 'north', 'south', 'west', 'east'];

export const FACE_SIZE = 16;

interface ModelFace {
  texture: string;
  tintindex?: number;
  rotation?: number;
}

interface ModelElement {
  from: number[];
  to: number[];
  faces: Partial<Record<FaceName, ModelFace>>;
}

export interface BlockModel {
  parent?: string;
  textures?: Record<string, string>;
  elements?: ModelElement[];
}

interface BlockstateVariant {
  model: string;
  y?: number;
}

export interface Blockstate {
  variants: Record<string, BlockstateVariant | BlockstateVariant[]>;
}

export interface FaceLayer {
  texture: string;
  tinted: boolean;
  /** Clockwise quarter turns of the texture on the face. */
  quarterTurns: number;
}

export type BlockFaces = Record<FaceName, FaceLayer[]>;

export interface ModelSource {
  blockstate(name: string): Promise<Blockstate>;
  model(name: string): Promise<BlockModel>;
}

const OUTWARD_EXTENT: Record<FaceName, (element: ModelElement) => number> = {
  up: (element) => element.to[1],
  down: (element) => -element.from[1],
  north: (element) => -element.from[2],
  south: (element) => element.to[2],
  west: (element) => -element.from[0],
  east: (element) => element.to[0],
};

// A blockstate y turn of 90 moves the north face to the east, the way MC's ModelRotation spins it.
const HORIZONTAL_CLOCKWISE: FaceName[] = ['north', 'east', 'south', 'west'];

function faceBeforeTurn(face: FaceName, quarterTurns: number): FaceName {
  const index = HORIZONTAL_CLOCKWISE.indexOf(face);
  if (index < 0) return face;
  return HORIZONTAL_CLOCKWISE[(index - quarterTurns + 4) % 4];
}

function resolveTexture(textures: Record<string, string>, reference: string): string | null {
  let current = reference;
  for (let depth = 0; current.startsWith('#'); depth++) {
    if (depth > 8) return null;
    const next = textures[current.slice(1)];
    if (next === undefined) return null;
    current = next;
  }
  return current.replace(/^(minecraft:)?blocks\//, '');
}

async function flattenModel(source: ModelSource, name: string) {
  const textures: Record<string, string> = {};
  let elements: ModelElement[] | undefined;
  let next: string | undefined = name;
  while (next && !next.startsWith('builtin/')) {
    const model: BlockModel = await source.model(next.replace(/^block\//, ''));
    for (const [key, value] of Object.entries(model.textures ?? {})) textures[key] ??= value;
    elements ??= model.elements;
    next = model.parent;
  }
  return { textures, elements: elements ?? [] };
}

/**
 * A side shows the faces of the elements reaching furthest out on it, stacked in element order (grass
 * lays its tinted fringe over dirt that way). A side no element covers, as on a cross model, shows the particle.
 */
export async function resolveBlockFaces(source: ModelSource, location: string): Promise<BlockFaces> {
  const [stateName, variantKey] = location.split('#');
  const blockstate = await source.blockstate(stateName);
  const listed = blockstate.variants[variantKey];
  if (!listed) throw new Error(`Blockstate ${stateName} has no variant ${variantKey}`);
  const variant = Array.isArray(listed) ? listed[0] : listed;
  const turns = ((variant.y ?? 0) / 90) % 4;
  const { textures, elements } = await flattenModel(source, variant.model);
  const particle = resolveTexture(textures, '#particle');

  const faces = {} as BlockFaces;
  for (const face of FACE_ORDER) {
    const modelFace = faceBeforeTurn(face, turns);
    const extent = OUTWARD_EXTENT[modelFace];
    const covering = elements.filter((element) => element.faces[modelFace]);
    const outermost = Math.max(...covering.map(extent));
    // Up turns with the block; down is seen from below, so the same turn reads counterclockwise.
    const spin = face === 'up' ? turns : face === 'down' ? (4 - turns) % 4 : 0;
    const layers: FaceLayer[] = [];
    for (const element of covering) {
      if (extent(element) !== outermost) continue;
      const modelFaceEntry = element.faces[modelFace]!;
      const texture = resolveTexture(textures, modelFaceEntry.texture);
      if (!texture) continue;
      layers.push({
        texture,
        tinted: modelFaceEntry.tintindex !== undefined,
        quarterTurns: ((modelFaceEntry.rotation ?? 0) / 90 + spin) % 4,
      });
    }
    if (layers.length === 0 && particle) layers.push({ texture: particle, tinted: false, quarterTurns: 0 });
    faces[face] = layers;
  }
  return faces;
}

export function turnClockwise(raster: Raster, quarterTurns: number): Raster {
  let turned = raster;
  for (let turn = 0; turn < quarterTurns; turn++) {
    const size = turned.width;
    const data = new Uint8ClampedArray(turned.data.length);
    for (let pixelY = 0; pixelY < size; pixelY++) {
      for (let pixelX = 0; pixelX < size; pixelX++) {
        const from = (pixelY * size + pixelX) * 4;
        const to = (pixelX * size + (size - 1 - pixelY)) * 4;
        data.set(turned.data.subarray(from, from + 4), to);
      }
    }
    turned = { data, width: size, height: size };
  }
  return turned;
}

const fetchJson = async <T>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} answered ${response.status}`);
  return (await response.json()) as T;
};

const assetSource: ModelSource = {
  blockstate: (name) => fetchJson(`/assets/blockstates/${name}.json`),
  model: (name) => fetchJson(`/assets/models/block/${name}.json`),
};

const textureCache = new Map<string, Promise<Raster | null>>();

function loadFaceTexture(texture: string): Promise<Raster | null> {
  let pending = textureCache.get(texture);
  if (!pending) {
    pending = fetchRaster(blockTextureSrc(texture)).then((raster) => {
      if (!raster) return null;
      const frame = firstFrame(raster);
      if (frame.width % FACE_SIZE !== 0) return null;
      return shrinkBy(frame, frame.width / FACE_SIZE);
    });
    textureCache.set(texture, pending);
  }
  return pending;
}

async function bakeFace(layers: FaceLayer[]): Promise<Raster> {
  const face: Raster = { data: new Uint8ClampedArray(FACE_SIZE * FACE_SIZE * 4), width: FACE_SIZE, height: FACE_SIZE };
  for (const layer of layers) {
    const loaded = await loadFaceTexture(layer.texture);
    if (!loaded) continue;
    const raster = { ...loaded, data: loaded.data.slice() };
    const tint = layer.tinted ? TINTED_TEXTURES[layer.texture]?.tint : undefined;
    if (tint) multiplyTint(raster, TINT_DEFAULTS[tint]);
    drawOver(face, turnClockwise(raster, layer.quarterTurns));
  }
  return face;
}

const blockCache = new Map<string, Promise<Raster[]>>();

/** Six 16 pixel faces in `FACE_ORDER`, transparent where the block's own textures are. */
export function loadBlockFaces(location: string): Promise<Raster[]> {
  let pending = blockCache.get(location);
  if (!pending) {
    pending = resolveBlockFaces(assetSource, location).then((faces) =>
      Promise.all(FACE_ORDER.map((face) => bakeFace(faces[face]))),
    );
    blockCache.set(location, pending);
  }
  return pending;
}

/**
 * Lays each face over the block's map colour. The preview draws only the faces no neighbour hides, so
 * see-through pixels in glass or leaves would otherwise open onto the hollow inside of the build.
 */
export function overColour(face: Raster, colour: Rgb): Uint8Array {
  const opaque = new Uint8Array(face.data.length);
  for (let offset = 0; offset < face.data.length; offset += 4) {
    const alpha = face.data[offset + 3] / 255;
    for (let channel = 0; channel < 3; channel++) {
      opaque[offset + channel] = Math.round(face.data[offset + channel] * alpha + colour[channel] * (1 - alpha));
    }
    opaque[offset + 3] = 255;
  }
  return opaque;
}

export function solidFace(colour: Rgb): Uint8Array {
  const tile = new Uint8Array(FACE_SIZE * FACE_SIZE * 4);
  for (let offset = 0; offset < tile.length; offset += 4) tile.set([colour[0], colour[1], colour[2], 255], offset);
  return tile;
}

export const ATLAS_COLUMNS = 64;

export interface FaceAtlas {
  /** Level 0 first; every level halves the tiles down to one pixel each, so no level mixes two tiles. */
  levels: { data: Uint8Array<ArrayBuffer>; width: number; height: number }[];
  rows: number;
}

export function packAtlas(tiles: Uint8Array[]): FaceAtlas {
  const rows = Math.max(1, Math.ceil(tiles.length / ATLAS_COLUMNS));
  const width = ATLAS_COLUMNS * FACE_SIZE;
  const height = rows * FACE_SIZE;
  const base = new Uint8Array(width * height * 4);
  tiles.forEach((tile, tileIndex) => {
    const tileX = (tileIndex % ATLAS_COLUMNS) * FACE_SIZE;
    const tileY = Math.floor(tileIndex / ATLAS_COLUMNS) * FACE_SIZE;
    for (let rowIndex = 0; rowIndex < FACE_SIZE; rowIndex++) {
      const from = rowIndex * FACE_SIZE * 4;
      base.set(tile.subarray(from, from + FACE_SIZE * 4), ((tileY + rowIndex) * width + tileX) * 4);
    }
  });

  const levels = [{ data: base, width, height }];
  for (let tileSize = FACE_SIZE / 2; tileSize >= 1; tileSize /= 2) {
    const larger = levels[levels.length - 1];
    const levelWidth = larger.width / 2;
    const levelHeight = larger.height / 2;
    const data = new Uint8Array(levelWidth * levelHeight * 4);
    for (let pixelY = 0; pixelY < levelHeight; pixelY++) {
      for (let pixelX = 0; pixelX < levelWidth; pixelX++) {
        const topLeft = (pixelY * 2 * larger.width + pixelX * 2) * 4;
        const bottomLeft = topLeft + larger.width * 4;
        for (let channel = 0; channel < 4; channel++) {
          const sum =
            larger.data[topLeft + channel] +
            larger.data[topLeft + 4 + channel] +
            larger.data[bottomLeft + channel] +
            larger.data[bottomLeft + 4 + channel];
          data[(pixelY * levelWidth + pixelX) * 4 + channel] = Math.round(sum / 4);
        }
      }
    }
    levels.push({ data, width: levelWidth, height: levelHeight });
  }
  return { levels, rows };
}
